/**
 * Document Structure Export Utilities
 *
 * Exports folder/document structure to V2 JSON format for KG validation.
 * All API calls go through the documentApi service from ApiContext.
 */

interface ExportStructureOptions {
  download?: boolean;
  rootFolderId?: string;
}

// Huy's array-based structure format
interface StructureNode {
  name: string;
  type: 'folder' | 'file';
  md5?: string;
  metadata?: Record<string, unknown>;
  children?: StructureNode[];
}

interface StructureExportResponse extends StructureNode {
  // Root folder with metadata containing generated_at and namespace
  metadata?: {
    generated_at?: string;
    namespace?: string;
    [key: string]: unknown;
  };
}

/** Service interface matching EnhancedDocumentApiService.exportStructure() */
interface DocumentApiService {
  exportStructure(options?: { download?: boolean; rootFolderId?: string }): Promise<StructureExportResponse | Record<string, unknown>>;
}

/**
 * Fetch structure from backend API via documentApi service
 */
export const fetchStructure = async (
  documentApi: DocumentApiService,
  options: ExportStructureOptions = {}
): Promise<StructureExportResponse> => {
  return documentApi.exportStructure(options) as Promise<StructureExportResponse>;
};

/**
 * Download structure as JSON file
 */
export const downloadStructureJSON = async (documentApi: DocumentApiService, rootFolderId?: string): Promise<void> => {
  const structure = await fetchStructure(documentApi, { rootFolderId });
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  // Namespace is in metadata for Huy's format
  const namespace = structure.metadata?.namespace || 'default';
  const filename = `structure-${namespace}-${timestamp}.json`;

  const dataStr = JSON.stringify(structure, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  downloadFile(dataBlob, filename);
};

/**
 * Copy structure JSON to clipboard
 */
export const copyStructureToClipboard = async (documentApi: DocumentApiService, rootFolderId?: string): Promise<void> => {
  const structure = await fetchStructure(documentApi, { rootFolderId });
  const dataStr = JSON.stringify(structure, null, 2);
  await navigator.clipboard.writeText(dataStr);
};

/**
 * Trigger file download in browser
 */
const downloadFile = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Get structure stats for display (supports Huy's array-based format)
 */
export const getStructureStats = (structure: StructureExportResponse): { folders: number; documents: number } => {
  let folders = 0;
  let documents = 0;

  const countNodes = (node: StructureNode): void => {
    if (node.type === 'folder') {
      folders++;
      // Huy's format: children is an array
      if (Array.isArray(node.children)) {
        node.children.forEach(countNodes);
      }
    } else if (node.type === 'file') {
      documents++;
    }
  };

  // Huy's format: structure IS the root folder
  if (structure.type === 'folder') {
    countNodes(structure);
  }

  return { folders, documents };
};
